import type { ControllerSession } from '../controller';
import {
  fileChunkSize,
  FILE_READ_AHEAD_CHUNKS,
  MAX_FILE_NAME_LENGTH,
  MAX_FILE_TRANSFER_BYTES,
  FileTransferCancelledError,
  createFileTransferId,
  FILE_TRANSFER_MAX_RETRIES,
  FILE_TRANSFER_RETRY_DELAY_MS,
  FILE_TRANSFER_CHANNEL_WAIT_TIMEOUT_MS,
  FILE_TRANSFER_CHANNEL_POLL_MS,
  FILE_RESUME_GRACE_MS,
  FILE_RESUME_TIMEOUT_MS,
  FILE_ACK_INTERVAL_BYTES,
  FILE_ACK_SUPPORT_TIMEOUT_MS,
  FILE_ACK_WAIT_TIMEOUT_MS,
  FILE_ACK_WINDOW_HIGH_BYTES,
  FILE_ACK_WINDOW_LOW_BYTES,
  FILE_COMPLETION_ACK_TIMEOUT_MS,
  IncrementalSha256,
  fileAckBlockIndex,
  waitForFileChannelDrain,
  waitForFileAcknowledgedOffset,
  isRetryableFileTransferError,
  type FileClipboardAck,
  type FileControl,
  type FileAck,
  type FileMeta,
  type FileResumeRequest,
  type FileResumeResponse,
  type FileTransferProgress,
} from '../../shared/filexfer';
import {
  MAX_CLIPBOARD_FILE_BYTES,
  MAX_CLIPBOARD_FILE_COUNT,
  isClipboardImage,
  normalizeClipboardImageMimeType,
  type ClipboardFileData,
} from '../../shared/clipboard-file';
import {
  FILE_BUFFER_HIGH,
  FILE_BUFFER_LOW,
  FILE_RECEIVE_IDLE_TIMEOUT_MS,
  FILE_STREAM_WRITE_BATCH,
  type ReceivingFile,
} from './types';

function combineChunks(chunks: Uint8Array[], total: number): ArrayBuffer {
  const result = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return result.buffer;
}

function fileNameFromMeta(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const name = value.replace(/[\\/\u0000-\u001f]/g, '_').trim();
  return name && name.length <= MAX_FILE_NAME_LENGTH ? name : null;
}

function fileIdFromMeta(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  return /^[A-Za-z0-9._:-]{1,128}$/.test(value) ? value : null;
}

function fileCancelReason(value: unknown, fallback = '文件传输已取消'): string {
  return typeof value === 'string' && value.trim()
    ? value.replace(/[\r\n\t]+/g, ' ').trim().slice(0, 128)
    : fallback;
}

function clipboardMetadataFromFile(file: File | ClipboardFileData): Pick<FileMeta, 'kind' | 'mimeType'> {
  if (!('data' in file) || file.kind !== 'image') return {};
  const mimeType = normalizeClipboardImageMimeType(file.mimeType);
  return mimeType ? { kind: 'image', mimeType } : {};
}

function clipboardMetaValid(meta: FileMeta | FileResumeRequest, purpose: 'download' | 'clipboard'): boolean {
  if (purpose !== 'clipboard') return true;
  if (meta.kind !== undefined && meta.kind !== 'file' && meta.kind !== 'image') return false;
  if (meta.kind === 'image' && !normalizeClipboardImageMimeType(meta.mimeType)) return false;
  if (meta.kind !== 'image' && meta.mimeType !== undefined) return false;
  return Boolean(
    fileIdFromMeta(meta.clipboardId) &&
    Number.isSafeInteger(meta.clipboardIndex) &&
    Number.isSafeInteger(meta.clipboardTotal) &&
    meta.clipboardIndex! >= 0 &&
    meta.clipboardTotal! > 0 &&
    meta.clipboardTotal! <= MAX_CLIPBOARD_FILE_COUNT &&
    meta.clipboardIndex! < meta.clipboardTotal!,
  );
}

function fileEndHash(value: unknown): string | null | 'invalid' {
  if (value === undefined) return null;
  return typeof value === 'string' && /^[a-f0-9]{64}$/i.test(value)
    ? value.toLowerCase()
    : 'invalid';
}

function sameFileTransfer(meta: FileMeta | FileResumeRequest, received: ReceivingFile): boolean {
  const purpose = meta.purpose === 'clipboard' ? 'clipboard' : 'download';
  const kind = meta.kind === 'image' ? 'image' : undefined;
  const mimeType = kind ? normalizeClipboardImageMimeType(meta.mimeType) || undefined : undefined;
  return received.id === meta.id &&
    received.name === meta.name &&
    received.size === meta.size &&
    received.purpose === purpose &&
    received.kind === kind &&
    received.mimeType === mimeType &&
    received.clipboardId === meta.clipboardId &&
    received.clipboardIndex === meta.clipboardIndex &&
    received.clipboardTotal === meta.clipboardTotal;
}

export const controllerFile = {
  armFileReceiveTimeout(this: ControllerSession): void {
    this.clearFileReceiveTimeout();
    const received = this.fileRecv;
    if (!received) return;
    this.fileReceiveTimer = setTimeout(() => {
      if (this.fileRecv !== received) return;
      this.fileRecv = null;
      this.fileReceiveResumeUntil = 0;
      void this.cancelReceivedFileStream(received);
      this.sendFileCancel(received.id, '文件接收超时');
      this.cb.onFileTransfer?.({
        id: received.id,
        name: received.name,
        size: received.size,
        transferred: Math.min(received.received, received.size),
        direction: 'receive',
        state: 'failed',
        error: '长时间没有收到数据，已自动取消',
      });
      this.cb.onStatus('文件接收超时，已自动取消', 'error');
    }, FILE_RECEIVE_IDLE_TIMEOUT_MS);
  },

  clearFileReceiveTimeout(this: ControllerSession): void {
    if (!this.fileReceiveTimer) return;
    clearTimeout(this.fileReceiveTimer);
    this.fileReceiveTimer = null;
  },

  clearFileResumeWaiters(this: ControllerSession): void {
    for (const pending of this.pendingFileResumes.values()) {
      if (pending.timer) clearTimeout(pending.timer);
      pending.resolve(null);
    }
    this.pendingFileResumes.clear();
  },

  clearFileAckWaiters(this: ControllerSession): void {
    for (const pending of this.pendingFileAcks.values()) {
      if (pending.timer) clearTimeout(pending.timer);
      pending.resolve(null);
    }
    this.pendingFileAcks.clear();
  },

  clearFileAckWaiter(this: ControllerSession, id: string): void {
    const pending = this.pendingFileAcks.get(id);
    if (!pending) return;
    if (pending.timer) clearTimeout(pending.timer);
    this.pendingFileAcks.delete(id);
    pending.resolve(null);
  },

  waitForFileResume(this: ControllerSession, id: string): Promise<FileResumeResponse | null> {
    const existing = this.pendingFileResumes.get(id);
    if (existing) {
      if (existing.timer) clearTimeout(existing.timer);
      existing.resolve(null);
      this.pendingFileResumes.delete(id);
    }
    return new Promise<FileResumeResponse | null>((resolve) => {
      const timer = setTimeout(() => {
        if (this.pendingFileResumes.get(id)?.resolve !== resolve) return;
        this.pendingFileResumes.delete(id);
        resolve(null);
      }, FILE_RESUME_TIMEOUT_MS);
      this.pendingFileResumes.set(id, { id, resolve, timer });
    });
  },

  waitForFileChannel(this: ControllerSession, transferId?: string): Promise<RTCDataChannel> {
    const getOpenChannel = (): RTCDataChannel | null => {
      const channel = this.fileDc;
      return channel?.readyState === 'open' ? channel : null;
    };
    const ready = getOpenChannel();
    if (ready) return Promise.resolve(ready);
    return new Promise<RTCDataChannel>((resolve, reject) => {
      let pollTimer: ReturnType<typeof setInterval> | null = null;
      let timeoutTimer: ReturnType<typeof setTimeout> | null = null;
      const finish = (error?: Error, channel?: RTCDataChannel): void => {
        if (pollTimer) clearInterval(pollTimer);
        if (timeoutTimer) clearTimeout(timeoutTimer);
        pollTimer = null;
        timeoutTimer = null;
        if (error) reject(error);
        else if (channel) resolve(channel);
      };
      const check = (): void => {
        if (transferId && this.cancelledFileTransfers.has(transferId)) {
          finish(new FileTransferCancelledError());
          return;
        }
        if (this.disposed) {
          finish(new Error('连接已结束'));
          return;
        }
        const channel = getOpenChannel();
        if (channel) {
          finish(undefined, channel);
          return;
        }
        // timeoutTimer is the authoritative deadline; this branch only makes
        // the error deterministic if a host timer fires late or is throttled.
        if (Date.now() >= deadline) {
          finish(new Error('文件通道恢复超时，请重试'));
        }
      };
      const deadline = Date.now() + FILE_TRANSFER_CHANNEL_WAIT_TIMEOUT_MS;
      pollTimer = setInterval(check, FILE_TRANSFER_CHANNEL_POLL_MS);
      timeoutTimer = setTimeout(() => finish(new Error('文件通道恢复超时，请重试')), FILE_TRANSFER_CHANNEL_WAIT_TIMEOUT_MS);
      check();
    });
  },

  sendFileResumeResponse(this: ControllerSession, response: FileResumeResponse): void {
    const channel = this.fileDc;
    if (!channel || channel.readyState !== 'open') return;
    try {
      channel.send(JSON.stringify(response as FileControl));
    } catch {
      // 对端会在超时后回退到完整重传。
    }
  },

  sendFileAck(this: ControllerSession, message: FileAck): boolean {
    const channel = this.fileDc;
    if (!channel || channel.readyState !== 'open') return false;
    try {
      channel.send(JSON.stringify(message as FileControl));
      return true;
    } catch {
      return false;
    }
  },

  waitForFileCompletionAck(this: ControllerSession, id: string): Promise<boolean | null> {
    this.clearFileAckWaiter(id);
    return new Promise<boolean | null>((resolve) => {
      const timer = setTimeout(() => {
        if (this.pendingFileAcks.get(id)?.resolve !== resolve) return;
        this.pendingFileAcks.delete(id);
        // 没有完成回执通常意味着对端是旧版本；由调用方走兼容成功路径。
        resolve(null);
      }, FILE_COMPLETION_ACK_TIMEOUT_MS);
      this.pendingFileAcks.set(id, { id, resolve, timer });
    });
  },

  handleFileAck(this: ControllerSession, message: FileAck): void {
    const id = fileIdFromMeta(message.id);
    if (!id) return;
    // 中间回执就是可恢复的连续检查点。只接受单调递增的进度，
    // 完成回执则允许把检查点推进到最终偏移。
    const previousOffset = this.fileAckOffsets.get(id) || 0;
    if (message.offset >= previousOffset || message.done === true) {
      this.fileAckOffsets.set(id, message.offset);
    }
    if (this.activeFileTransfers.has(id)) this.fileProgressAckOffsets.set(id, message.offset);
    if (message.done !== true) return;
    const pending = this.pendingFileAcks.get(id);
    if (!pending) return;
    if (pending.timer) clearTimeout(pending.timer);
    this.pendingFileAcks.delete(id);
    pending.resolve(message.accepted !== false);
  },

  sendFileProgressAck(this: ControllerSession, received: ReceivingFile, force = false): void {
    if (received.purpose !== 'download') return;
    if (!force && received.received - received.acknowledgedOffset < FILE_ACK_INTERVAL_BYTES) return;
    if (received.streamId && received.streamBufferedBytes > 0) return;
    if (this.sendFileAck({
      type: 'file-ack',
      id: received.id,
      offset: received.received,
      blockIndex: fileAckBlockIndex(received.received),
    })) {
      received.acknowledgedOffset = received.received;
    }
  },

  handleFileResumeResponse(this: ControllerSession, response: FileResumeResponse): void {
    const id = fileIdFromMeta(response.id);
    if (!id || !this.pendingFileResumes.has(id)) return;
    if (
      typeof response.accepted !== 'boolean' ||
      !Number.isSafeInteger(response.offset) ||
      response.offset < 0
    ) return;
    const pending = this.pendingFileResumes.get(id);
    if (!pending) return;
    this.pendingFileResumes.delete(id);
    if (pending.timer) clearTimeout(pending.timer);
    // 续传响应来自接收端的实际连续偏移，可能比本地最后一条回执更小
    // （例如通道在回执抵达前中断），因此这里必须以远端值覆盖旧检查点。
    this.fileAckOffsets.set(id, response.accepted ? response.offset : 0);
    pending.resolve(response);
  },

  requestFileResume(
    this: ControllerSession,
    file: File | ClipboardFileData,
    extra: Pick<FileMeta, 'purpose' | 'clipboardId' | 'clipboardIndex' | 'clipboardTotal'>,
    id: string,
    channel: RTCDataChannel,
  ): Promise<FileResumeResponse | null> {
    const size = 'data' in file ? file.data.byteLength : file.size;
    const name = fileNameFromMeta(file.name) || file.name;
    const wait = this.waitForFileResume(id);
    try {
      channel.send(JSON.stringify({
        type: 'file-resume-request',
        id,
        name,
        size,
        ...extra,
        ...clipboardMetadataFromFile(file),
        ...(this.fileAckOffsets.get(id)
          ? { checkpointOffset: this.fileAckOffsets.get(id) }
          : {}),
      } as FileResumeRequest));
    } catch {
      const pending = this.pendingFileResumes.get(id);
      if (pending) {
        if (pending.timer) clearTimeout(pending.timer);
        this.pendingFileResumes.delete(id);
        pending.resolve(null);
      }
    }
    return wait;
  },

  async flushReceivedFileStream(this: ControllerSession, received: ReceivingFile): Promise<void> {
    if (!received.streamId || !this.api.appendFileReceive || received.streamBufferedBytes <= 0) return;
    const data = combineChunks(received.streamBuffer, received.streamBufferedBytes);
    const result = await this.api.appendFileReceive(received.streamId, data);
    if (!result.ok) throw new Error(result.error || '文件写入失败');
    received.streamBuffer = [];
    received.streamBufferedBytes = 0;
  },

  async cancelReceivedFileStream(this: ControllerSession, received: ReceivingFile): Promise<void> {
    const streamId = received.streamId;
    received.streamId = undefined;
    received.streamBuffer = [];
    received.streamBufferedBytes = 0;
    if (!streamId || !this.api.cancelFileReceive) return;
    try {
      await this.api.cancelFileReceive(streamId);
    } catch {
      // 进程退出或 IPC 关闭时，主进程会在清理阶段回收临时文件。
    }
  },

  setupFileChannel(this: ControllerSession): void {
    if (!this.fileDc) return;
    this.fileMessageGuard.reset();
    this.fileDc.binaryType = 'arraybuffer';
    const channel = this.fileDc;
    channel.onclose = () => {
      if (this.fileDc !== channel) return;
      this.fileDc = null;
      // 文件通道可能单独失败，而控制通道仍然是 open；主动触发一次
      // ICE 恢复，避免发送端永远轮询一个已经关闭的旧通道。
      if (!this.disposed) this.scheduleIceRestart(500);
    };
    channel.onmessage = (ev) => {
      // DataChannel 事件本身按序到达，但处理函数包含文件保存/剪贴板
      // 异步操作；串行化后 file-end 不会抢在最后一块数据之前执行。
      this.fileMessageTail = this.fileMessageTail
        .then(() => this.onFileMessage(ev.data, channel))
        .catch((error) => console.warn('[controller file] message failed:', error));
    };
  },

  async onFileMessage(this: ControllerSession, data: unknown, sourceChannel?: RTCDataChannel): Promise<void> {
    if (sourceChannel && this.fileDc !== sourceChannel) return;
    if (this.accessMode === 'view') return;
    this.markConnectionActivity();
    if (typeof data === 'string') {
      let msg: FileControl;
      const parsed = this.fileMessageGuard.parse(data);
      if (!parsed) return;
      msg = parsed as unknown as FileControl;
      if (msg.type === 'file-clipboard-ack') {
        this.finishFileClipboardTransfer(msg.id, msg.ok);
        if (!msg.ok) this.cb.onStatus(msg.reason || '文件剪贴板写入失败，请重试', 'error');
        return;
      }
      if (msg.type === 'file-cancel') {
        this.handleFileCancel(msg);
        return;
      }
      if (msg.type === 'file-resume') {
        this.handleFileResumeResponse(msg);
        return;
      }
      if (msg.type === 'file-ack') {
        this.handleFileAck(msg);
        return;
      }
      if (msg.type === 'file-resume-request') {
        const id = fileIdFromMeta(msg.id);
        const name = fileNameFromMeta(msg.name);
        const size = Number(msg.size);
        const purpose = msg.purpose === 'clipboard' ? 'clipboard' : 'download';
        const received = this.fileRecv;
        const accepted = Boolean(
          id && name && Number.isSafeInteger(size) && size >= 0 && size <= MAX_FILE_TRANSFER_BYTES &&
          clipboardMetaValid(msg, purpose) && received && sameFileTransfer(msg, received),
        );
        if (accepted && received) {
          this.fileReceiveResumeUntil = Date.now() + FILE_RESUME_GRACE_MS;
          this.armFileReceiveTimeout();
          if (received.purpose === 'download') {
            this.sendFileAck({
              type: 'file-ack',
              id: received.id,
              offset: received.received,
              blockIndex: fileAckBlockIndex(received.received),
            });
          }
          this.cb.onFileTransfer?.({
            id: received.id,
            name: received.name,
            size: received.size,
            transferred: received.received,
            direction: 'receive',
            state: 'transferring',
          });
        }
        if (id) {
          this.sendFileResumeResponse({
            type: 'file-resume',
            id,
            offset: accepted && received ? received.received : 0,
            accepted,
            ...(accepted ? {} : { reason: '没有可恢复的文件任务' }),
          });
        }
        return;
      }
      if (msg.type === 'file-meta') {
        const purpose = msg.purpose === 'clipboard' ? 'clipboard' : 'download';
        const size = Number.isFinite(msg.size) && msg.size >= 0 ? msg.size : -1;
        const id = fileIdFromMeta(msg.id);
        const name = fileNameFromMeta(msg.name);
        const requestedOffset = msg.offset === undefined ? 0 : Number(msg.offset);
        if (
          !id || !name || !Number.isSafeInteger(size) || size < 0 || size > MAX_FILE_TRANSFER_BYTES ||
          !Number.isSafeInteger(requestedOffset) || requestedOffset < 0 || requestedOffset > size ||
          !clipboardMetaValid(msg, purpose)
        ) {
          if (id) this.sendFileCancel(id, size > MAX_FILE_TRANSFER_BYTES ? '文件超过 1GB 限制' : '文件信息无效');
          this.cb.onStatus(size > MAX_FILE_TRANSFER_BYTES ? '文件超过 1GB 限制，已取消接收' : '收到无效文件，已取消接收', 'error');
          return;
        }
        if (this.fileRecv) {
          if (
            sameFileTransfer(msg, this.fileRecv) &&
            requestedOffset === this.fileRecv.received &&
            (requestedOffset > 0 || msg.resume === true)
          ) {
            this.fileReceiveResumeUntil = 0;
            this.armFileReceiveTimeout();
            this.cb.onFileTransfer?.({
              id: this.fileRecv.id,
              name: this.fileRecv.name,
              size: this.fileRecv.size,
              transferred: this.fileRecv.received,
              direction: 'receive',
              state: 'transferring',
            });
            return;
          }
          // 旧版本发送端不带 offset。若这是刚刚保留下来的旧任务，
          // 让旧端从头开始时安全替换分片，避免一直卡在“已有文件正在接收”。
          if (this.fileReceiveResumeUntil > Date.now()) {
            const previous = this.fileRecv;
            this.fileRecv = null;
            this.fileReceiveResumeUntil = 0;
            this.clearFileReceiveTimeout();
            await this.cancelReceivedFileStream(previous);
          }
        }
        if (this.fileRecv) {
          this.sendFileCancel(id, '已有文件正在接收');
          return;
        }
        if (requestedOffset > 0) {
          this.sendFileCancel(id, '没有可恢复的文件任务');
          return;
        }
        let streamId: string | undefined;
        let resumedBytes = 0;
        if (purpose === 'download' && this.api.beginFileReceive) {
          try {
            const result = await this.api.beginFileReceive(name, size, id, msg.resume === true);
            if (result.ok && result.transferId && fileIdFromMeta(result.transferId)) {
              streamId = result.transferId;
              resumedBytes = Number.isSafeInteger(result.resumedBytes)
                ? Math.min(Math.max(result.resumedBytes || 0, 0), size)
                : 0;
            }
          } catch {
            // 没有可用的本地流式写入能力时，继续使用浏览器/旧版缓冲保存。
          }
        }
        this.fileRecv = {
          id,
          name,
          size,
          purpose,
          kind: msg.kind === 'image' ? 'image' : undefined,
          mimeType: msg.kind === 'image' ? normalizeClipboardImageMimeType(msg.mimeType) || undefined : undefined,
          clipboardId: msg.clipboardId,
          clipboardIndex: msg.clipboardIndex,
          clipboardTotal: msg.clipboardTotal,
          chunks: [],
          streamId,
          streamResumed: resumedBytes > 0,
          streamBuffer: [],
          streamBufferedBytes: 0,
          received: resumedBytes,
          acknowledgedOffset: resumedBytes,
          hash: new IncrementalSha256(),
        };
        if (purpose === 'download') this.sendFileProgressAck(this.fileRecv, true);
        this.fileReceiveResumeUntil = 0;
        this.armFileReceiveTimeout();
        this.cb.onFileTransfer?.({
          id: msg.id,
          name: msg.name,
          size,
          transferred: resumedBytes,
          direction: 'receive',
          state: 'transferring',
        });
        if (resumedBytes > 0) {
          this.sendFileResumeResponse({
            type: 'file-resume',
            id,
            offset: resumedBytes,
            accepted: true,
          });
        }
        if (purpose === 'clipboard' && msg.clipboardId && msg.clipboardTotal && msg.clipboardTotal > 0) {
          if (!this.fileClipboardBatch || this.fileClipboardBatch.id !== msg.clipboardId) {
            this.fileClipboardBatch = {
              id: msg.clipboardId,
              total: msg.clipboardTotal,
              files: Array.from({ length: msg.clipboardTotal }, () => null),
            };
          }
        }
      } else if (msg.type === 'file-end' && this.fileRecv && this.fileRecv.id === msg.id) {
        const received = this.fileRecv;
        this.clearFileReceiveTimeout();
        if (received.streamId) {
          try {
            await this.flushReceivedFileStream(received);
          } catch (error) {
            const message = error instanceof Error ? error.message : '文件写入失败';
            await this.cancelReceivedFileStream(received);
            this.sendFileCancel(received.id, message);
            this.cb.onFileTransfer?.({
              id: received.id,
              name: received.name,
              size: received.size,
              transferred: Math.min(received.received, received.size),
              direction: 'receive',
              state: 'failed',
              error: message,
            });
            this.cb.onStatus(`文件保存失败：${message}`, 'error');
            this.fileRecv = null;
            this.fileReceiveResumeUntil = 0;
            return;
          }
        }
        this.sendFileProgressAck(received, true);
        this.fileRecv = null;
        this.fileReceiveResumeUntil = 0;
        const expectedHash = fileEndHash(msg.sha256);
        if (expectedHash === 'invalid') {
          this.cb.onFileTransfer?.({
            id: received.id,
            name: received.name,
            size: received.size,
            transferred: Math.min(received.received, received.size),
            direction: 'receive',
            state: 'failed',
            error: '文件校验信息无效，请重试',
          });
          await this.cancelReceivedFileStream(received);
          this.sendFileCancel(received.id, '文件校验信息无效');
          if (received.purpose === 'clipboard' && received.clipboardId) {
            this.sendFileClipboardAck(received.clipboardId, false, '文件校验信息无效');
          }
          return;
        }
        if (expectedHash && !received.streamResumed && received.hash.digestHex() !== expectedHash) {
          this.cb.onFileTransfer?.({
            id: received.id,
            name: received.name,
            size: received.size,
            transferred: Math.min(received.received, received.size),
            direction: 'receive',
            state: 'failed',
            error: '文件校验失败，请重试',
          });
          await this.cancelReceivedFileStream(received);
          this.sendFileCancel(received.id, '文件校验失败');
          if (received.purpose === 'clipboard' && received.clipboardId) {
            this.sendFileClipboardAck(received.clipboardId, false, '文件校验失败');
          }
          return;
        }
        let buf: ArrayBuffer | null = null;
        if (received.streamId) {
          if (received.received !== received.size) {
            await this.cancelReceivedFileStream(received);
            this.cb.onFileTransfer?.({
              id: received.id,
              name: received.name,
              size: received.size,
              transferred: Math.min(received.received, received.size),
              direction: 'receive',
              state: 'failed',
              error: '文件传输不完整',
            });
            this.sendFileCancel(received.id, '文件传输不完整');
            return;
          }
        } else {
          buf = this.combineReceivedFile(received);
        }
        if (!received.streamId && (!buf || buf.byteLength !== received.size)) {
          this.cb.onFileTransfer?.({
            id: received.id,
            name: received.name,
            size: received.size,
            transferred: Math.min(received.received, received.size),
            direction: 'receive',
            state: 'failed',
            error: '文件传输不完整',
          });
          this.sendFileCancel(received.id, '文件传输不完整');
          if (received.purpose === 'clipboard' && received.clipboardId) {
            this.sendFileClipboardAck(received.clipboardId, false, '文件传输不完整');
          }
          return;
        }

        if (
          received.purpose === 'clipboard' &&
          received.clipboardId &&
          this.fileClipboardBatch?.id === received.clipboardId
        ) {
          const index = Number.isInteger(received.clipboardIndex) ? received.clipboardIndex! : -1;
          if (index < 0 || index >= this.fileClipboardBatch.total) {
            this.sendFileClipboardAck(received.clipboardId, false, '文件批次无效');
            this.fileClipboardBatch = null;
            return;
          }
          const existingSize = this.fileClipboardBatch.files.reduce((total, file) => total + (file?.size || 0), 0);
          const previousSize = this.fileClipboardBatch.files[index]?.size || 0;
          if (existingSize - previousSize + received.size > MAX_CLIPBOARD_FILE_BYTES) {
            this.sendFileCancel(received.id, '文件剪贴板总大小超过 1GB');
            this.sendFileClipboardAck(received.clipboardId, false, '文件剪贴板总大小超过 1GB');
            this.fileClipboardBatch = null;
            return;
          }
          this.fileClipboardBatch.files[index] = {
            name: received.name,
            size: received.size,
            kind: received.kind,
            mimeType: received.mimeType,
            data: buf!,
          };
          if (this.fileClipboardBatch.files.every(Boolean)) {
            const batch = this.fileClipboardBatch;
            this.fileClipboardBatch = null;
            const files = batch.files.filter((file): file is ClipboardFileData => file !== null);
            let copied = false;
            try {
              if (files.length === 1 && isClipboardImage(files[0]) && this.api.writeClipboardImage) {
                copied = await this.api.writeClipboardImage(files[0].data, files[0].mimeType || 'image/png');
                if (!copied) {
                  await this.api.saveFile(files[0].name, files[0].data);
                  copied = true;
                }
              } else if (this.api.writeClipboardFiles) {
                copied = await this.api.writeClipboardFiles(files);
              } else {
                for (const file of files) await this.api.saveFile(file.name, file.data);
                copied = true; // 浏览器端以下载作为文件剪贴板的降级行为。
              }
            } catch {
              copied = false;
            }
            const infos = files.map(({ name, size, kind, mimeType }) => ({ name, size, kind, mimeType }));
            this.cb.onClipboardFiles?.(infos, copied);
            for (const file of files) {
              this.cb.onFileTransfer?.({
                id: received.id,
                name: file.name,
                size: file.size,
                transferred: file.size,
                direction: 'receive',
                state: copied ? 'completed' : 'failed',
                error: copied ? undefined : '浏览器未能开始下载',
              });
            }
          this.sendFileClipboardAck(batch.id, copied, copied ? undefined : '对端不支持系统文件剪贴板');
          }
          return;
        }

        if (received.streamId) {
          const streamId = received.streamId;
          try {
            const result = await this.api.finishFileReceive?.(streamId, expectedHash || undefined);
            if (!result?.ok) throw new Error(result?.error || '文件保存失败');
            received.streamId = undefined;
            this.sendFileAck({
              type: 'file-ack',
              id: received.id,
              offset: received.size,
              blockIndex: fileAckBlockIndex(received.size),
              done: true,
              accepted: true,
            });
            this.cb.onFileTransfer?.({
              id: received.id,
              name: received.name,
              size: received.size,
              transferred: received.size,
              direction: 'receive',
              state: 'completed',
            });
            this.cb.onStatus('已接收文件', 'online');
          } catch (error) {
            const message = error instanceof Error ? error.message : '文件保存失败';
            await this.cancelReceivedFileStream(received);
            this.sendFileCancel(received.id, message);
            this.cb.onFileTransfer?.({
              id: received.id,
              name: received.name,
              size: received.size,
              transferred: Math.min(received.received, received.size),
              direction: 'receive',
              state: 'failed',
              error: message,
            });
            this.cb.onStatus(`文件保存失败：${message}`, 'error');
          }
          return;
        }

        try {
          if (!buf) throw new Error('文件传输不完整');
          await this.api.saveFile(received.name, buf);
          this.sendFileAck({
            type: 'file-ack',
            id: received.id,
            offset: received.size,
            blockIndex: fileAckBlockIndex(received.size),
            done: true,
            accepted: true,
          });
          this.cb.onFileTransfer?.({
            id: received.id,
            name: received.name,
            size: received.size,
            transferred: received.size,
            direction: 'receive',
            state: 'completed',
          });
          this.fileRecv = null;
          this.fileReceiveResumeUntil = 0;
          this.cb.onStatus('已接收文件', 'online');
        } catch (error) {
          this.cb.onFileTransfer?.({
            id: received.id,
            name: received.name,
            size: received.size,
            transferred: Math.min(received.received, received.size),
            direction: 'receive',
            state: 'failed',
            error: error instanceof Error ? error.message : '文件保存失败',
          });
          this.fileRecv = null;
          this.fileReceiveResumeUntil = 0;
          this.cb.onStatus('文件保存失败，请重试', 'error');
        }
      }
    } else if (data instanceof ArrayBuffer && this.fileRecv) {
      const received = this.fileRecv;
      const chunk = new Uint8Array(data);
      if (received.received + chunk.byteLength > received.size) {
        this.fileRecv = null;
        this.fileReceiveResumeUntil = 0;
        void this.cancelReceivedFileStream(received);
        this.sendFileCancel(received.id, '文件大小超过声明值');
        this.cb.onFileTransfer?.({
          id: received.id,
          name: received.name,
          size: received.size,
          transferred: received.received,
          direction: 'receive',
          state: 'failed',
          error: '文件大小异常，已取消接收',
        });
        return;
      }
      received.hash.update(chunk);
      received.received += chunk.byteLength;
      if (received.streamId) {
        received.streamBuffer.push(chunk);
        received.streamBufferedBytes += chunk.byteLength;
        if (received.streamBufferedBytes >= FILE_STREAM_WRITE_BATCH || received.received === received.size) {
          try {
            await this.flushReceivedFileStream(received);
          } catch (error) {
            const message = error instanceof Error ? error.message : '文件写入失败';
            this.fileRecv = null;
            this.fileReceiveResumeUntil = 0;
            await this.cancelReceivedFileStream(received);
            this.sendFileCancel(received.id, message);
            this.cb.onFileTransfer?.({
              id: received.id,
              name: received.name,
              size: received.size,
              transferred: Math.min(received.received, received.size),
              direction: 'receive',
              state: 'failed',
              error: message,
            });
            this.cb.onStatus(`文件保存失败：${message}`, 'error');
            return;
          }
        }
      } else {
        received.chunks.push(chunk);
      }
      this.sendFileProgressAck(received, received.received === received.size);
      this.cb.onFileTransfer?.({
        id: received.id,
        name: received.name,
        size: received.size,
        transferred: Math.min(received.received, received.size),
        direction: 'receive',
        state: 'transferring',
      });
      if (this.fileRecv === received) this.armFileReceiveTimeout();
    }
  },

  async sendFile(this: ControllerSession, file: File, transferId?: string): Promise<void> {
    if (this.accessMode === 'view') return;
    await this.enqueueFileSend(async () => {
      const id = transferId || createFileTransferId();
      let lastTransferred = 0;
      const emit = (transferred: number, state: FileTransferProgress['state'], error?: string): void => {
        this.cb.onFileTransfer?.({
          id,
          name: file.name,
          size: file.size,
          transferred: Math.min(Math.max(transferred, 0), file.size),
          direction: 'send',
          state,
          error,
        });
      };
      for (let attempt = 0; ; attempt += 1) {
        emit(lastTransferred, 'transferring');
        try {
          await this.sendFilePayload(
            file,
            { purpose: 'download' },
            id,
            (transferred) => {
              lastTransferred = transferred;
              emit(transferred, 'transferring');
            },
            attempt > 0,
          );
          emit(file.size, 'completed');
          this.fileAckOffsets.delete(id);
          this.cb.onStatus('文件已发送', 'online');
          return;
        } catch (error) {
          const message = error instanceof Error ? error.message : '文件发送失败';
          const cancelled = error instanceof FileTransferCancelledError;
          if (
            !cancelled &&
            attempt < FILE_TRANSFER_MAX_RETRIES &&
            !this.disposed &&
            isRetryableFileTransferError(error)
          ) {
            this.cb.onStatus(
              `连接出现波动，正在恢复文件传输（${attempt + 1}/${FILE_TRANSFER_MAX_RETRIES}）`,
              'idle',
            );
            this.activeFileTransfers.add(id);
            await new Promise<void>((resolve) => setTimeout(resolve, FILE_TRANSFER_RETRY_DELAY_MS * (attempt + 1)));
            this.activeFileTransfers.delete(id);
            if (this.cancelledFileTransfers.has(id)) {
              this.cancelledFileTransfers.delete(id);
              throw new FileTransferCancelledError();
            }
            if (this.disposed) throw error;
            continue;
          }
          emit(lastTransferred, cancelled ? 'cancelled' : 'failed', message);
          this.fileAckOffsets.delete(id);
          this.cb.onStatus(cancelled ? '文件传输已取消' : `文件发送失败：${message}`, cancelled ? 'idle' : 'error');
          throw error;
        }
      }
    });
  },

  cancelFileTransfer(this: ControllerSession, id: string, reason = '已取消'): void {
    const normalizedId = fileIdFromMeta(id);
    if (!normalizedId) return;
    this.fileAckOffsets.delete(normalizedId);
    this.fileProgressAckOffsets.delete(normalizedId);
    this.cancelledFileTransfers.add(normalizedId);
    const pendingAck = this.pendingFileAcks.get(normalizedId);
    if (pendingAck) {
      if (pendingAck.timer) clearTimeout(pendingAck.timer);
      this.pendingFileAcks.delete(normalizedId);
      pendingAck.resolve(false);
    }
    this.sendFileCancel(normalizedId, reason);
    const received = this.fileRecv;
    if (received?.id !== normalizedId) {
      if (!this.activeFileTransfers.has(normalizedId)) this.cancelledFileTransfers.delete(normalizedId);
      return;
    }
    this.clearFileReceiveTimeout();
    void this.cancelReceivedFileStream(received);
    this.fileRecv = null;
    this.fileReceiveResumeUntil = 0;
    this.cb.onFileTransfer?.({
      id: received.id,
      name: received.name,
      size: received.size,
      transferred: Math.min(received.received, received.size),
      direction: 'receive',
      state: 'cancelled',
      error: fileCancelReason(reason),
    });
    this.cancelledFileTransfers.delete(normalizedId);
  },

  sendFileCancel(this: ControllerSession, id: string, reason = '文件传输已取消'): void {
    const channel = this.fileDc;
    if (!channel || channel.readyState !== 'open') return;
    try {
      channel.send(JSON.stringify({ type: 'file-cancel', id, reason: fileCancelReason(reason) } as FileControl));
    } catch {
      // 连接关闭时对端会通过 DataChannel close 清理当前文件。
    }
  },

  handleFileCancel(this: ControllerSession, message: { type: 'file-cancel'; id: string; reason?: string }): void {
    const id = fileIdFromMeta(message.id);
    if (!id) return;
    this.fileAckOffsets.delete(id);
    this.fileProgressAckOffsets.delete(id);
    const reason = fileCancelReason(message.reason);
    const pendingAck = this.pendingFileAcks.get(id);
    if (pendingAck) {
      if (pendingAck.timer) clearTimeout(pendingAck.timer);
      this.pendingFileAcks.delete(id);
      pendingAck.resolve(false);
    }
    this.cancelledFileTransfers.add(id);
    const received = this.fileRecv;
    if (received?.id === id) {
      this.clearFileReceiveTimeout();
      void this.cancelReceivedFileStream(received);
      this.fileRecv = null;
      this.fileReceiveResumeUntil = 0;
      this.cb.onFileTransfer?.({
        id: received.id,
        name: received.name,
        size: received.size,
        transferred: Math.min(received.received, received.size),
        direction: 'receive',
        state: 'cancelled',
        error: reason,
      });
    }
    if (!this.activeFileTransfers.has(id)) this.cancelledFileTransfers.delete(id);
    this.cb.onStatus(reason, 'idle');
  },

  enqueueFileSend<T>(this: ControllerSession, job: () => Promise<T>): Promise<T> {
    const run = this.fileSendTail.then(job, job);
    this.fileSendTail = run.then(() => undefined, () => undefined);
    return run;
  },

  async sendFilePayload(
    this: ControllerSession,
    file: File | ClipboardFileData,
    extra: Pick<FileMeta, 'purpose' | 'clipboardId' | 'clipboardIndex' | 'clipboardTotal'>,
    transferId = createFileTransferId(),
    onProgress?: (transferred: number) => void,
    allowResume = false,
  ): Promise<void> {
    if (this.accessMode === 'view') return;
    const isClipboardFile = 'data' in file;
    const size = isClipboardFile ? file.data.byteLength : file.size;
    const chunkSize = fileChunkSize(this.pc?.sctp?.maxMessageSize);
    const id = transferId;
    if (!fileIdFromMeta(id)) throw new Error('文件传输编号无效');
    if (!Number.isSafeInteger(size) || size < 0 || size > MAX_FILE_TRANSFER_BYTES) {
      throw new Error('文件超过 1GB 限制');
    }
    const name = fileNameFromMeta(file.name);
    if (!name) throw new Error('文件名无效或过长');
    this.activeFileTransfers.add(id);
    this.cancelledFileTransfers.delete(id);
    this.fileProgressAckOffsets.delete(id);
    const hash = new IncrementalSha256();
    try {
      const channel = await this.waitForFileChannel(id);
      channel.bufferedAmountLowThreshold = FILE_BUFFER_LOW;
      let offset = 0;
      let lateResume: Promise<FileResumeResponse | null> | null = null;
      // 新任务不应继承同编号的旧回执；重试则保留它，供恢复请求携带最近检查点。
      if (!allowResume) this.fileAckOffsets.delete(id);
      if (allowResume) {
        const response = await this.requestFileResume(file, extra, id, channel);
        if (response?.accepted && response.offset <= size) offset = response.offset;
      }
      if (channel !== this.fileDc || channel.readyState !== 'open') throw new Error('文件通道已断开');
      if (allowResume) lateResume = this.waitForFileResume(id);
      const meta: FileMeta = {
        type: 'file-meta',
        id,
        name,
        size,
        ...extra,
        ...clipboardMetadataFromFile(file),
        ...(allowResume ? { resume: true } : {}),
        ...(offset > 0 ? { offset, resume: true } : {}),
      };
      channel.send(JSON.stringify(meta as FileControl));
      if (lateResume) {
        const response = await lateResume;
        if (response?.accepted && response.offset > offset && response.offset <= size) {
          offset = response.offset;
          if (channel !== this.fileDc || channel.readyState !== 'open') throw new Error('文件通道已断开');
          channel.send(JSON.stringify({ ...meta, offset, resume: true } as FileControl));
        }
      }
      const initialAck = extra.purpose === 'download'
        ? await waitForFileAcknowledgedOffset(
            channel,
            () => this.fileProgressAckOffsets.get(id),
            0,
            () => this.cancelledFileTransfers.has(id),
            FILE_ACK_SUPPORT_TIMEOUT_MS,
          )
        : null;
      const usesRemoteProgressAck = initialAck !== null;
      onProgress?.(usesRemoteProgressAck ? Math.min(offset, initialAck) : offset);
      // 续传时不再把已确认的前缀发回网络，但仍要纳入最终摘要校验。
      let hashedPrefix = 0;
      while (hashedPrefix < offset) {
        if (this.cancelledFileTransfers.has(id)) throw new FileTransferCancelledError();
        const buf = isClipboardFile
          ? file.data.slice(hashedPrefix, Math.min(offset, hashedPrefix + chunkSize))
          : await file.slice(hashedPrefix, Math.min(offset, hashedPrefix + chunkSize)).arrayBuffer();
        if (buf.byteLength === 0) throw new Error('读取文件失败');
        hash.update(buf);
        hashedPrefix += buf.byteLength;
      }
      const readChunk = async (chunkOffset: number): Promise<ArrayBuffer> => {
        if (isClipboardFile) return file.data.slice(chunkOffset, chunkOffset + chunkSize);
        return file.slice(chunkOffset, chunkOffset + chunkSize).arrayBuffer();
      };
      const pendingChunks = new Map<number, Promise<ArrayBuffer>>();
      let nextReadOffset = offset;
      const scheduleRead = (chunkOffset: number): void => {
        if (chunkOffset >= size || pendingChunks.has(chunkOffset)) return;
        pendingChunks.set(chunkOffset, readChunk(chunkOffset));
      };
      for (let index = 0; index < FILE_READ_AHEAD_CHUNKS && nextReadOffset < size; index += 1) {
        scheduleRead(nextReadOffset);
        nextReadOffset += chunkSize;
      }
      while (offset < size) {
        if (this.cancelledFileTransfers.has(id)) throw new FileTransferCancelledError();
        await this.waitForFileDrain(channel, id);
        if (this.cancelledFileTransfers.has(id)) throw new FileTransferCancelledError();
        if (channel !== this.fileDc || channel.readyState !== 'open') throw new Error('文件通道已断开');
        const pending = pendingChunks.get(offset) || readChunk(offset);
        pendingChunks.delete(offset);
        scheduleRead(nextReadOffset);
        nextReadOffset += chunkSize;
        const buf = await pending;
        if (buf.byteLength === 0) throw new Error('读取文件失败');
        hash.update(buf);
        channel.send(buf);
        offset += buf.byteLength;
        if (usesRemoteProgressAck) {
          const acknowledgedOffset = Math.min(offset, this.fileProgressAckOffsets.get(id) || 0);
          onProgress?.(acknowledgedOffset);
          if (offset - acknowledgedOffset >= FILE_ACK_WINDOW_HIGH_BYTES) {
            const nextAcknowledgedOffset = await waitForFileAcknowledgedOffset(
              channel,
              () => this.fileProgressAckOffsets.get(id),
              offset - FILE_ACK_WINDOW_LOW_BYTES,
              () => this.cancelledFileTransfers.has(id),
              FILE_ACK_WAIT_TIMEOUT_MS,
            );
            onProgress?.(Math.min(offset, nextAcknowledgedOffset || 0));
          }
        } else {
          onProgress?.(offset);
        }
      }
      await this.waitForFileDrain(channel, id);
      if (this.cancelledFileTransfers.has(id)) throw new FileTransferCancelledError();
      if (channel !== this.fileDc || channel.readyState !== 'open') throw new Error('文件通道已断开');
      const completionAck = extra.purpose === 'clipboard'
        ? null
        : this.waitForFileCompletionAck(id);
      channel.send(JSON.stringify({ type: 'file-end', id, sha256: hash.digestHex() } as FileControl));
      if (completionAck) {
        const result = await completionAck;
        if (result === false || (result === null && channel.readyState !== 'open')) {
          throw new Error(result === false ? '远端保存文件失败' : '文件通道已断开');
        }
      }
    } catch (error) {
      this.clearFileAckWaiter(id);
      if (!this.cancelledFileTransfers.has(id)) {
          this.sendFileCancel(id, error instanceof Error ? error.message : '文件发送失败');
      }
      throw error;
    } finally {
      this.activeFileTransfers.delete(id);
      this.cancelledFileTransfers.delete(id);
      this.fileProgressAckOffsets.delete(id);
    }
  },

  combineReceivedFile(this: ControllerSession, file: ReceivingFile): ArrayBuffer | null {
    if (file.received < 0 || file.received > file.size) return null;
    const result = new Uint8Array(file.received);
    let offset = 0;
    for (const chunk of file.chunks) {
      if (offset + chunk.byteLength > result.byteLength) return null;
      result.set(chunk, offset);
      offset += chunk.byteLength;
    }
    return result.buffer;
  },

  sendFileClipboardAck(this: ControllerSession, id: string, ok: boolean, reason?: string): void {
    const channel = this.fileDc;
    if (!channel || channel.readyState !== 'open') return;
    try {
      channel.send(JSON.stringify({ type: 'file-clipboard-ack', id, ok, reason } as FileClipboardAck));
    } catch {
      // 发送端会在超时后报告失败。
    }
  },

  finishFileClipboardTransfer(this: ControllerSession, id: string, ok: boolean): void {
    const pending = this.pendingFileClipboardTransfers.get(id);
    if (!pending) return;
    this.pendingFileClipboardTransfers.delete(id);
    if (pending.timer) clearTimeout(pending.timer);
    pending.resolve(ok);
  },

  clearFileClipboardTransfers(this: ControllerSession): void {
    for (const pending of this.pendingFileClipboardTransfers.values()) {
      if (pending.timer) clearTimeout(pending.timer);
      pending.resolve(false);
    }
    this.pendingFileClipboardTransfers.clear();
  },

  async waitForFileDrain(this: ControllerSession, channel: RTCDataChannel, transferId?: string): Promise<void> {
    await waitForFileChannelDrain(
      channel,
      FILE_BUFFER_HIGH,
      FILE_BUFFER_LOW,
      () => Boolean(transferId && this.cancelledFileTransfers.has(transferId)),
    );
  },
};
